import { useState, useMemo } from "react";
import { useMutation, useQueryClient } from "react-query";
import { enqueueSnackbar } from "notistack";
import {
  Box,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
} from "@mui/material";

import { CardProps } from "..";
import { formatarValorMonetario } from "../../../../../../helpers/masks";
import { TPagamentoDetalhes, TParametrosPagamento, TParcelasPagamento } from "../../../../../../types/api/comercial/detalhar-pedido";
import axiosClient from "../../../../../../lib/axios";

import DialogPix from "../../../../../../components/BasicComponents/Pagamentos/DialogPix";
import DialogDinheiro, { TPagamentoDinheiroParams } from "../../../../../../components/BasicComponents/Pagamentos/DialogDinheiro";
import DialogCartao, { TPagamentoCartaoParams } from "../../../../../../components/BasicComponents/Pagamentos/DialogCartao";
import DialogCheque, { TPagamentoChequeParams } from "../../../../../../components/BasicComponents/Pagamentos/DialogCheque";
import DialogConfirmacao from "../../../../../../components/BasicComponents/Pagamentos/DialogConfirmacao";
import SituacaoPagamentoChip from "../../../../../../components/SituacaoPagamento";
import { usePermission } from "../../../../../../components/PermissionGuard";

export default function StepPagamento({ data }: StepPagamentoProps) {
  const queryClient = useQueryClient();

  const hasPermission = usePermission("comercial.realizar-pagamento");
  // Estados
  const [openDialog, setOpenDialog] = useState<TDialogsPagamentos>({
    modal: null,
    valor: 0,
    open: false,
    id: null,
    nomeFormaRecebimento: undefined,
  });

  // Mutations
  const { mutate: receberPagamento, isLoading } = useMutation({
    mutationKey: "receber-pagamento",
    mutationFn: (input: any) =>
      axiosClient.post(`/comercial/receber-pagamento/${input.id}`, input),
    onError: (error) => {
      console.error(error);
      enqueueSnackbar("Erro ao receber o pagamento!", { variant: "error" });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(`comercial/detalhar-pedido`);
      enqueueSnackbar("Pagamento recebido com sucesso!", {
        variant: "success",
      });
    },
  });

  const { mutate: registrarPagamentos, isLoading: loadingRegistrar } = useMutation({
    mutationKey: `registrar-pagamentos-${data.codigo}`,
    mutationFn: () =>
      axiosClient.post(`/comercial/registrar-pagamentos-pedido/${data.codigo}`),
    onError: (error) => {
      console.error(error);
      enqueueSnackbar("Erro ao registrar os pagamentos!", {
        variant: "error",
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(`comercial/detalhar-pedido`);
      enqueueSnackbar("Pagamentos registrados com sucesso!", {
        variant: "success",
      });
    },
  });

  // Funções auxiliares
  const verificarParcelas = (parametros: TParametrosPagamento) => {
    const quantidade = parametros.parcelas.length;
    const valor_total = parametros.parcelas.reduce(
      (acc, item) => acc + item.valor,
      0
    );
    return { quantidade, valor_total };
  };

  // Cálculos
  const calcularTotalPagamentos = useMemo(() => {
    return data.pagamentos.reduce((total, pagamento) => {
      if (pagamento.parametros && pagamento.parametros.parcelas.length > 0) {
        return total + verificarParcelas(pagamento.parametros).valor_total;
      }
      return total + pagamento.valor;
    }, 0);
  }, [data.pagamentos]);

  const totalPedido = useMemo(() => data.total, [data.total]);

  const valoresDivergem = useMemo(() => {
    const diferenca = Math.abs(calcularTotalPagamentos - totalPedido);
    return diferenca > 0.01;
  }, [calcularTotalPagamentos, totalPedido]);

  // Handlers
  const handleChangeDialogState = (value: TDialogsPagamentos) => {
    setOpenDialog(value);
  };

  const handleReceberPagamento = (
    id: number,
    pgto: TPagamentoCartaoParams | TPagamentoDinheiroParams | TPagamentoChequeParams | null,
  ) => {
    const pagamento = { pagamento: pgto, id };
    receberPagamento(pagamento);
  };

  const handleAdicionarModalPagamento = (item: TPagamentoDetalhes) => {
    const { forma_recebimento } = item;
    const valor = !item.parametros || !item.parametros.parcelas
      ? item.valor
      : verificarParcelas(item.parametros).valor_total;

    const baseDialogState = {
      open: true,
      valor,
      id: item.id,
    };

    // PIX
    if (forma_recebimento.flg_usa_pix === "S") {
      handleChangeDialogState({
        ...baseDialogState,
        modal: "PIX",
      });
      return;
    }

    // Cartão de Crédito
    if (forma_recebimento.flg_cartao_credito === "S") {
      handleChangeDialogState({
        ...baseDialogState,
        modal: "CARTAO",
      });
      return;
    }

    // Cheque
    if (forma_recebimento.flg_cheque === "S") {
      handleChangeDialogState({
        ...baseDialogState,
        modal: "CHEQUE",
        parcelas: item.parametros.parcelas,
      });
      return;
    }

    // Dinheiro
    if (
      forma_recebimento.flg_usa_pix === "N" &&
      forma_recebimento.flg_cheque === "N" &&
      forma_recebimento.flg_cartao_credito === "N" &&
      (forma_recebimento.testa_limite_mpg === " " || forma_recebimento.testa_limite_mpg === "N")
      && forma_recebimento.conta_contabil['A'] !== 25
    ) {
      handleChangeDialogState({
        ...baseDialogState,
        modal: "DINHEIRO",
      });
      return;
    }

    // Confirmação
    if (
      (forma_recebimento.flg_usa_pix === "N" &&
        forma_recebimento.flg_cheque === "N" &&
        forma_recebimento.flg_cartao_credito === "N" &&
        forma_recebimento.testa_limite_mpg === "G") || forma_recebimento.conta_contabil['A'] === 25
    ) {
      handleChangeDialogState({
        ...baseDialogState,
        modal: "CONFIRMACAO",
        nomeFormaRecebimento: forma_recebimento.nome,
      });
      return;
    }
  };





  // Renderização do loading
  const renderLoading = () => (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      py={3}
    >
      <CircularProgress />
      <Box mt={2} fontSize="1rem" color="text.secondary">
        Registrando os pagamentos, por favor aguarde...
      </Box>
    </Box>
  );

  // Renderização da tabela de pagamentos
  const renderTabelaPagamentos = () => (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell><b>Forma de recebimento</b></TableCell>
            <TableCell align="right"><b>Valor</b></TableCell>
            <TableCell><b>Situação</b></TableCell>
            <TableCell></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.pagamentos.map((item: TPagamentoDetalhes, index) => {
            const temParcelas = item.parametros && item.parametros.parcelas.length > 1;
            const parcelasInfo = temParcelas ? verificarParcelas(item.parametros) : null;

            return (
              <TableRow
                key={index}
                sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
              >
                <TableCell>{item.forma_recebimento.nome}</TableCell>
                <TableCell align="right">
                  {temParcelas
                    ? `${formatarValorMonetario(parcelasInfo!.valor_total)} (${parcelasInfo!.quantidade}x)`
                    : formatarValorMonetario(verificarParcelas(item.parametros).valor_total)
                  }
                </TableCell>
                <TableCell>
                  <SituacaoPagamentoChip situacao={item.status} />
                </TableCell>
                <TableCell>
                  {hasPermission && (
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={item.status === "Pago" || isLoading}
                      onClick={() => handleAdicionarModalPagamento(item)}
                    >
                      {isLoading && <CircularProgress size={20} sx={{ mr: 1 }} />}
                      Confirmar
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderAlertaDivergencia = () => (
    <Alert severity="warning" sx={{ mb: 2 }}>
      <strong>Atenção:</strong> O valor total dos pagamentos ({formatarValorMonetario(calcularTotalPagamentos)})
      não confere com o valor total do pedido ({formatarValorMonetario(totalPedido)}).
      Por favor, atualize as formas de recebimento para continuar.
    </Alert>
  );

  const todosPagamentosPagos = data.pagamentos.every(
    (item: TPagamentoDetalhes) => item.status === "Pago"
  );

  return (
    <Box mt={2}>
      {loadingRegistrar ? renderLoading() : renderTabelaPagamentos()}

      {valoresDivergem && renderAlertaDivergencia()}

      {hasPermission && (
        <Button
          sx={{ mt: 2 }}
          variant="contained"
          disabled={!todosPagamentosPagos || loadingRegistrar || valoresDivergem}
          onClick={() => registrarPagamentos()}
        >
          Avançar
        </Button>
      )}
      {/* Modais de Pagamento */}
      {openDialog.open && (
        <>
          {openDialog.modal === "PIX" && openDialog.id && (
            <DialogPix
              open={openDialog.open}
              routes={{
                route: "/comercial/pedidos/emitir-pix",
                routeParams: { pedido_pagamento_id: openDialog.id },
                aguardandoPagamentoRoute: "/comercial/pedidos/consultar-emissao-pix/",
                aguardandoPagamentoCodigo: openDialog.id,
              }}
              tipo_publico={data.tipo_publico}
              codigo_publico={data.codigo_publico}
              onClose={() => {
                handleChangeDialogState({
                  open: false,
                  modal: null,
                  valor: 0,
                  id: null,
                });
                queryClient.invalidateQueries(`comercial/detalhar-pedido`);
              }}
              mensagem={`Segue abaixo o código PIX no valor de ${formatarValorMonetario(
                openDialog.valor
              )} para o pagamento do pedido #${data.codigo}:\n`}
            />
          )}

          {openDialog.modal === "DINHEIRO" && openDialog.id && (
            <DialogDinheiro
              open={openDialog.open}
              onClose={() => handleChangeDialogState({
                open: false,
                modal: null,
                valor: 0,
                id: null,
              })}
              onSubmit={(data) => {
                handleReceberPagamento(openDialog.id!, data);
                handleChangeDialogState({
                  open: false,
                  modal: null,
                  valor: 0,
                  id: null,
                });
              }}
              valor={openDialog.valor}
              defaultValues={
                openDialog.pagamento
                  ? (openDialog.pagamento as TPagamentoDinheiroParams)
                  : undefined
              }
              isEdit={!!openDialog.pagamento}
            />
          )}

          {openDialog.modal === "CONFIRMACAO" && openDialog.id && (
            <DialogConfirmacao
              open={openDialog.open}
              onClose={() => handleChangeDialogState({
                open: false,
                modal: null,
                valor: 0,
                id: null,
                nomeFormaRecebimento: undefined,
              })}
              onSubmit={() => {
                handleReceberPagamento(openDialog.id!, null);
                handleChangeDialogState({
                  open: false,
                  modal: null,
                  valor: 0,
                  id: null,
                  nomeFormaRecebimento: undefined,
                });
              }}
              valor={openDialog.valor}
              nomeFormaRecebimento={openDialog.nomeFormaRecebimento}
            />
          )}

          {openDialog.modal === "CARTAO" && openDialog.id && (
            <DialogCartao
              open={openDialog.open}
              onClose={() => handleChangeDialogState({
                open: false,
                modal: null,
                valor: 0,
                id: null,
              })}
              onSubmit={(data) => {
                handleReceberPagamento(openDialog.id!, data);
                handleChangeDialogState({
                  open: false,
                  modal: null,
                  valor: 0,
                  id: null,
                });
              }}
              valor={openDialog.valor}
              pagamentoId={openDialog.id}
              defaultValues={
                openDialog.pagamento
                  ? (openDialog.pagamento as TPagamentoCartaoParams)
                  : undefined
              }
              isEdit={!!openDialog.pagamento}
              onReceberPagamento={handleReceberPagamento}
            />
          )}

          {openDialog.modal === "CHEQUE" && openDialog.id && openDialog.parcelas && (
            <DialogCheque
              open={openDialog.open}
              onClose={() => handleChangeDialogState({
                open: false,
                modal: null,
                valor: 0,
                id: null,
              })}
              onSubmit={(data) => {
                handleReceberPagamento(openDialog.id!, data);
                handleChangeDialogState({
                  open: false,
                  modal: null,
                  valor: 0,
                  id: null,
                });
              }}
              valor={openDialog.valor}
              defaultValues={
                openDialog.pagamento
                  ? (openDialog.pagamento as TPagamentoChequeParams)
                  : {
                    valor: openDialog.valor,
                    cheque_banco: data.publico.conta_cheque?.banco || null,
                    cheque_agencia: data.publico.conta_cheque?.agencia || "",
                    cheque_conta: data.publico.conta_cheque?.conta || "",
                    cheque_conta_digito: data.publico.conta_cheque?.conta_digito || "",
                    cheque_documento: data.publico.documento_pessoa,
                    cheque_nome_emissor: data.publico.nome_pessoa,
                    cheque_praca: data.loja_faturamento.cidade,
                    cheque_agencia_digito: "",
                    cheque_seq: "",
                    departamento_centro_custo: null,
                    parcelas: openDialog.parcelas.map((item) => ({
                      valor: item.valor,
                      data_vencimento: item.vencimento,
                      cheque_num: "",
                    })),
                  }
              }
              isEdit={!!openDialog.pagamento}
            />
          )}
        </>
      )}
    </Box>
  );
}

// Tipos
interface StepPagamentoProps extends CardProps { }

type TDialogsPagamentos = {
  open: boolean;
  modal: TModals;
  valor: number;
  pagamento?: TPagamentoChequeParams | TPagamentoDinheiroParams | TPagamentoCartaoParams | null;
  parcelas?: TParcelasPagamento[];
  id: number | null;
  nomeFormaRecebimento?: string;
};

type TModals = "PIX" | "DINHEIRO" | "CARTAO" | "BOLETO" | "CHEQUE" | "CONFIRMACAO" | null;

export type TValorDialogPagamentos = {
  valor: number;
};
